/**
 * @file 给普通组件提供的基础类，基础函数
 * @author dongtiancheng
 */

import {IsString} from 'class-validator';
import {actionCreators} from './action';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import {CSSProperties} from 'react';
import {get, isNil, isPlainObject} from 'lodash';
import * as moment from 'moment';
import {ContainerConfig} from '../../../components/Container/Container';
import {compileExpressionString, isExpression} from '../../util/vm';
import {TriggerEventItem} from '../Trigger/Trigger';
import {DataCustomer} from '../DataCustomer/Controller';
import { RCREOptions } from '../Page';

export type rawJSONType = string | number | null | boolean | Object;
export type originJSONType = rawJSONType | rawJSONType[];

export type defaultData = {
    [s: string]: originJSONType
};

export class BasicConfig {
    /**
     * 组件类型
     */
    @IsString()
    type: string;

    /**
     * 是否隐藏
     */
    hidden?: boolean;

    /**
     * 字级数据Key
     */
    name?: string;

    /**
     * 是否显示
     */
    show?: boolean;

    /**
     * 父级属性映射
     */
    parentMapping?: Object;

    /**
     * 子级元素
     */
    children?: BasicConfig[];

    /**
     * CSS class
     */
    @IsString()
    className?: string;

    /**
     * 内联CSS属性
     */
    style?: CSSProperties;

    /**
     * 事件触发
     */
    trigger?: TriggerEventItem[];

    /**
     * 是否作为表单输入元素
     */
    formItem?: boolean;

    [s: string]: any;
}

export class BasicContainerPropsInterface {
    info: BasicConfig;

    /**
     * 当前Container的数据模型对象
     */
    $data?: Object;

    /**
     * 父级Container组件的数据模型对象
     */
    $parent?: Object;

    /**
     * RCRE 渲染配置
     */
    options?: RCREOptions;

    /**
     * Trigger组件的数据模型对象
     */
    $trigger?: Object;

    /**
     * 通过表格组件, 渲染之后, 获取到的每一行的数据
     */
    $item?: Object;

    /**
     * 通过表格组件, 渲染之后, 获取到的第几行
     */
    $index?: number;

    /**
     * 表单状态变量
     */
    $form?: { [s: string]: any };

    /**
     * React组件Key
     */
    key?: string | number;

    /**
     * 底层组件设置数据模型值使用
     */
    $setData?: (name: string, value: any) => void;

    /**
     * 底层组件清除某个字段的数据
     */
    $deleteData?: (name: string) => void;

    /**
     * 底层组件设置多组数据模型值
     */
    $setMultiData?: (items: {name: string, value: any}[]) => void;

    /**
     * Trigger注入的通用事件处理函数, 所有事件处理都走这里
     */
    eventHandle?: (eventName: string, args: Object) => void;

    /**
     * 来自Container的数据消耗者实例
     */
    dataCustomer?: DataCustomer;

    /**
     * 父级的数据模型Key
     */
    model?: string;
}

export class ContainerProps extends BasicContainerPropsInterface {
    info: ContainerConfig;

    $data: {};
    $parent: {};

    options: RCREOptions;

    /**
     * 写入数据到数据模型
     */
    setData: typeof actionCreators.setData;

    /**
     * 批量写入一组数据
     */
    setDataList: typeof actionCreators.setDataList;

    /**
     * 批量写入多组不同key的数据
     */
    setMultiData: typeof actionCreators.setMultiData;

    /**
     * 清空当前数据模型
     */
    clearData: typeof actionCreators.clearData;

    /**
     * 删除数据模型某一个字段
     */
    deleteData: typeof actionCreators.deleteData;

    /**
     * 异步加载数据中
     */
    asyncLoadDataProgress: typeof actionCreators.asyncLoadDataProgress;

    /**
     * 异步加载数据成功
     */
    asyncLoadDataSuccess: typeof actionCreators.asyncLoadDataSuccess;

    /**
     * 异步加载数据失败
     */
    asyncLoadDataFail: typeof actionCreators.asyncLoadDataFail;

    /**
     * 同步加载数据成功
     */
    syncLoadDataSuccess: typeof actionCreators.syncLoadDataSuccess;

    /**
     * 同步加载数据失败
     */
    syncLoadDataFail: typeof actionCreators.syncLoadDataFail;
}

export const BasicContextTypes = {
    driver: PropTypes.object,
    form: PropTypes.bool,
    abstractContainer: PropTypes.bool,
    $store: PropTypes.object,
    $global: PropTypes.object,
    $location: PropTypes.object,
    $query: PropTypes.object,
    debug: PropTypes.bool,
    lang: PropTypes.string
};

export type runTimeType = {
    $data?: Object;
    $query?: Object;
    $global?: any;
    $item?: Object;
    $trigger?: {
        $SELF_PASS_CUSTOMER?: Object;
    };
    $index?: number;
    $now?: moment.Moment;
    $moment: typeof moment;
    $args?: Object;
    $output?: Object;
    $iterator?: Object;
    $parent?: Object;
    $form?: Object;
    $prev?: any;
};

/**
 * 获取ExpressionString 嵌入的上下文
 * @param {BasicContainerPropsInterface} props
 * @param context
 * @return {runTimeType}
 */
export function getRuntimeContext(props: BasicContainerPropsInterface, context: any): runTimeType {
    let runtime: runTimeType = {
        $now: moment(),
        $moment: moment
    };

    if (props.$data) {
        runtime.$data = props.$data;
    }

    if (props.$parent) {
        runtime.$parent = props.$parent;
    }

    if (props.$item) {
        runtime.$item = props.$item;
    }

    if (!isNil(props.$index)) {
        runtime.$index = props.$index;
    }

    if (props.$trigger) {
        runtime.$trigger = props.$trigger;
    }

    if (props.$form) {
        runtime.$form = props.$form;
    }

    if (context.$query) {
        runtime.$query = context.$query;
    }

    if (context.$global) {
        runtime.$global = context.$global;
    }

    return runtime;
}

/**
 * 所有子级组件的基类
 */
export abstract class BasicContainer<T extends BasicContainerPropsInterface, P> extends React.Component<T, P> {
    static contextTypes = BasicContextTypes;
    private depESProps: { [s: string]: string };
    private computedCache: { [s: string]: any };
    private cacheBlackList: string[];

    constructor(props: T) {
        super(props);

        this.depESProps = {};
        this.computedCache = {};
        this.cacheBlackList = [
            'type',
            'hidden',
            'show',
            'model',
            'name',
            'dataProvider',
            'dataCustomer',
            'children'
        ];

        let info = this.props.info;

        for (let key in info) {
            if (
                info.hasOwnProperty(key) &&
                isExpression(info[key]) &&
                this.cacheBlackList.indexOf(key) < 0
            ) {
                this.depESProps[key] = info[key];
                this.computedCache[key] = null;
            }
        }
    }

    componentWillUnmount() {
        let info = this.getPropsInfo(this.props.info);
        if (this.props.$deleteData && info.name && this.props.$form) {
            this.props.$deleteData(info.name);
        }
    }

    shouldComponentUpdate(nextProps: T, nextState: P) {
        return true;
    }

    /**
     * 获取当前组件的ExpressionString 内嵌变量
     *
     * @param {T} props React组件的Props
     * @param context React组件Context
     * @returns {runTimeType}
     */
    protected getRuntimeContext(props: T = this.props, context: any = this.context) {
        let runTime = getRuntimeContext(props, context);
        return runTime;
    }

    /**
     * 调用此函数来解析JSON配置中的ExpressionString指令
     *
     * @param {InfoType} info 组件的JSON配置
     * @param {T} props React组件的Props
     * @param {string[]} blackList 属性黑名单，指定之后不处理ExpressionString
     * @param {boolean} isDeep 是否深度遍历JSON配置
     * @param {string[]} whiteList 属性白名单，如果指定之后，只处理白名单里面的属性值
     * @returns {InfoType}
     */
    protected getPropsInfo<InfoType>(info: InfoType,
                                     props?: T,
                                     blackList?: string[],
                                     isDeep?: boolean,
                                     whiteList?: string[]) {
        // do not deep copy info. it is dangerous, Find another way out!!!
        let runTime = this.getRuntimeContext(props);
        info = compileExpressionString(info, runTime, blackList, isDeep, whiteList);

        if (isPlainObject(info['style'])) {
            info['style'] = compileExpressionString(info['style'], runTime);
        }

        return info;
    }

    /**
     * 错误处理函数，用于返回一些可预见的错误。
     *
     * @param {string} msg
     * @param extendElement
     * @returns {React.DetailedReactHTMLElement<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>}
     */
    protected errorReport(msg: string, extendElement: any) {
        if (this.context.debug) {
            return React.createElement(extendElement, {}, msg);
        } else {
            console.error(msg);
            return React.createElement(extendElement);
        }
    }

    /**
     * 渲染组件，用于支持hidden属性，可让组件隐藏
     *
     * @param {BasicConfig} info 组件的JSON配置
     * @param {React.ReactElement} children 输出的React组件
     * @returns {any}
     */
    protected renderChildren(info: BasicConfig, children: React.ReactNode) {
        if (info.hidden === true || info.show === false) {
            return (
                <div key={info.type + '_' + Math.random()} style={{display: 'none'}} />
            );
        }

        return children;
    }

    /**
     * 自动获取用于传递给子级组件的props属性
     *
     * @param {BasicConfig} info
     * @param {Object} childProps
     * @returns {BasicContainerPropsInterface}
     */
    protected getChildProps(info: BasicConfig, childProps: Object) {
        return {
            info: info,
            $data: this.props.$data,
            $parent: this.props.$parent,
            model: this.props.model,
            dataCustomer: this.props.dataCustomer,
            $setData: this.props.$setData,
            eventHandle: this.props.eventHandle,
            $index: this.props.$index,
            $item: this.props.$item,
            ...childProps
        };
    }

    /**
     * 事件注册器，注册一个事件，可以使用trigger属性来调用
     *
     * @param {string} eventName 事件名称
     * @param {{[p: string]: any}} args 额外的参数，会注入到param属性中的$args对象
     * @param {boolean} mute
     */
    protected commonEventHandler(eventName: string, args: {
        [s: string]: any
    }, mute: boolean = true) {
        if (this.props.eventHandle) {
            this.props.eventHandle(eventName, args);
        } else if (!mute) {
            if (this.props.$data) {
                console.error('If you want to handle event, you need to at trigger property');
            } else {
                console.error('Event System can only work with Container Component');
            }
        }
    }

    /**
     * 替换this.props.$data.get(name)这样的用法，来支持name属性中带有.分割的情况
     *
     * @param {string} nameStr 组件的name属性
     * @returns {any}
     */
    protected getValueFromDataStore(nameStr: string, $data?: Object) {
        $data = $data || this.props.$data;

        if (!$data) {
            return null;
        }

        return get($data, nameStr);
    }

    /**
     * 组件是否处于container组件内部
     * @returns {boolean}
     */
    protected isUnderContainerEnv() {
        return !!this.props.$data && !!this.props.$setData;
    }

    /**
     *
     * @returns {boolean}
     */
    protected isDebugMode() {
        return !!this.context.debug;
    }

    /**
     * 当使用扩展组件来使用的时候才提供参数，正常不提供参数
     * @param {any[]} args
     * @param {(event: any) => Object} callback
     * @returns {Object}
     */
    protected getExternalCallbackArgs(args: any[], callback?: (event: any) => Object): Object {
        if (callback) {
            return callback.apply(this, args) || {};
        } else {
            return {};
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/render/core/Container/types.tsx